import { isSuppressed } from '@/types/domain'
import type {
  ExpenseLine,
  LegLevelMoney,
  LegUnit,
  Maybe,
  Money,
  UnitKind,
  UnitMoney,
} from '@/types/domain'

/* The rows of «Оплатить перевозчику» as data (S-18a): one row per unit — every
 * kind since S-18b, which the sheet groups under «Фуры», «Контейнеры»… — then
 * «Плечо целиком», each saying WHICH single target it pays and under WHICH
 * carrier it is listed. Kept out of the sheet so each answer is pinned by a
 * test rather than through a rendered form (`carrierPayRows.test.ts`).
 *
 * ── Which single target a row pays (Locked Decision) ────────────────────────
 *
 * A unit row pays its single unrealised PLAN (a debt naming `planLineId`),
 * else its single ACTUAL (`expenseLineId`, the amount defaulted to the line's
 * served `remaining`), else the unit itself (an unplanned debt naming
 * `unitId`). «Плечо целиком» is the same rule over the leg's lines with no
 * unit, ending in `wholeLeg: true`. A unit — or the leg level — with several
 * actuals, or several unrealised plans, has no single target: its row is
 * refused with «Несколько счетов — оплатите на экране денег», because which of
 * them the money settles is a decision (AD-11) the money screen asks for.
 *
 * ── The carrier a row is listed under (the server's carrier rule) ───────────
 *
 * Several items in one payment must all be ONE directory carrier's, judged by
 * the server (`carrierRule`, 422 `items: carrier-mismatch`) as: an existing
 * actual → its stored vendor; a unit debt → the unit's OWN carrier, for any
 * kind, with no fallback to the leg's; a leg-level target → the leg's carrier. This file
 * lists each row under exactly that carrier, so the sheet never offers a set
 * the server would refuse. A row whose carrier is no directory id — a typed
 * name, nobody, or withheld (AD-6) — is listed under «Перевозчик не из
 * справочника» (`OFF_DIRECTORY`), where one row at a time is all one payment
 * may carry (a single item needs no carrier).
 *
 * ── A debt is booked to a DIRECTORY carrier (S-22d) ────────────────────────
 *
 * A row that recognises a debt books it to the carrier the server's
 * `carrierFor` picks — the unit's own carrier when it has one (an id or a
 * typed name), else the leg's, else nobody (then the payee). The server
 * refuses a debt whose picked carrier is TYPED ONLY (422 `items.<i>.debt:
 * carrier-not-in-directory`), so this file marks exactly those rows
 * (`carrierNotInDirectory`) and the sheet does not let them be ticked: say
 * where to fix it — the unit's carrier, or the leg's. Paying an existing
 * actual is unaffected: its line already names its vendor.
 *
 * Nothing here adds money. The figures are served (`remaining`, the plan's
 * own figure, the per-unit and leg-level rollups) and only passed through.
 */

/** The «Плечо целиком» row's key — no unit id can collide with it. */
export const WHOLE_LEG = '__whole-leg__'

/** The carrier key of every row whose carrier is no directory participant. */
export const OFF_DIRECTORY = '__off-directory__'

/** The one thing a row pays, or `null` when it has several candidates. */
export type PayTarget =
  /** A debt realising this plan — `debt.planLineId`. */
  | { kind: 'plan'; line: ExpenseLine }
  /** This recorded actual — `expenseLineId: line.actualId`. */
  | { kind: 'actual'; line: ExpenseLine }
  /** An unplanned debt — `debt.unitId`, or `debt.wholeLeg` on the leg row. */
  | { kind: 'new' }

export interface PayRow {
  /** The unit's id, or `WHOLE_LEG`. */
  key: string
  /** The unit's id; absent on «Плечо целиком». */
  unitId?: string
  /** The unit's kind — the group the sheet lists it under; absent on
   *  «Плечо целиком». */
  kind?: UnitKind
  /** The unit's number; `''` on «Плечо целиком», which the sheet labels
   *  itself. The group heading names the kind. */
  label: string
  target: PayTarget | null
  /** A directory participant's id, or `OFF_DIRECTORY`. */
  carrierKey: string
  /** The carrier's name as served, `''` when none or withheld. */
  carrierName: string
  /** The single actual reads `paid` (served `settlement`) — greyed, not
   *  checkable, tagged «оплачено». */
  paid: boolean
  /** The row would book a debt to a TYPED-ONLY carrier, which the server
   *  refuses (S-22d) — not checkable, and the reason says where to fix it:
   *  `unit` (the unit's own carrier) or `leg` (the leg's, which a unit with no
   *  carrier of its own falls back to, and which «Плечо целиком» uses).
   *  Absent on a row that pays an existing actual or has no single target. */
  carrierNotInDirectory?: 'unit' | 'leg'
  /** What the row prints under «Остаток»: the plan's own figure, the actual's
   *  served `remaining`, else the unit's (or the leg level's) USD rollup. */
  remainder?: Money
  /** What a checked row's amount defaults to — only while it is in the
   *  payment's currency, which the sheet checks. */
  seed?: Money
}

export interface PayRowsInput {
  units: readonly LegUnit[]
  /** The leg money block's `expenses` — folded, plan beside actual. */
  expenses: readonly ExpenseLine[]
  perUnit: readonly UnitMoney[]
  legLevel?: LegLevelMoney
  legCarrierId?: Maybe<string>
  legCarrierName?: Maybe<string>
}

/** A directory id, or `OFF_DIRECTORY` for '', absent or withheld. */
function carrierKeyOf(id: Maybe<string> | null | undefined): string {
  return typeof id === 'string' && id !== '' ? id : OFF_DIRECTORY
}

function nameOf(name: Maybe<string> | null | undefined): string {
  return typeof name === 'string' && !isSuppressed(name) ? name : ''
}

interface CarrierRef {
  id?: Maybe<string> | null
  name?: Maybe<string> | null
}

/** A carrier at all — a directory id or a typed name: the server's
 *  `carrier.key() != ""`. Withheld counts as none (it cannot be told). */
function hasCarrier(carrier: CarrierRef): boolean {
  return carrierKeyOf(carrier.id) !== OFF_DIRECTORY || nameOf(carrier.name).trim() !== ''
}

/** A carrier with a typed name and no directory id. */
function typedOnly(carrier: CarrierRef): boolean {
  return carrierKeyOf(carrier.id) === OFF_DIRECTORY && nameOf(carrier.name).trim() !== ''
}

/** How many recorded actuals a folded line stands for. */
function actualCount(line: ExpenseLine): number {
  if (line.actualId !== undefined) return 1
  if (line.actual !== undefined) return line.actualLines ?? 1
  return 0
}

/** The single target among one unit's (or the leg level's) lines. */
function targetOf(lines: readonly ExpenseLine[]): PayTarget | null {
  const plans = lines.filter(
    (line) => line.actualId === undefined && line.actual === undefined && !line.unplanned,
  )
  const actuals = lines.reduce((count, line) => count + actualCount(line), 0)
  if (plans.length > 1 || actuals > 1) return null
  if (plans.length === 1) return { kind: 'plan', line: plans[0]! }
  if (actuals === 1) {
    const line = lines.find((candidate) => candidate.actualId !== undefined)
    return line ? { kind: 'actual', line } : null
  }
  return { kind: 'new' }
}

/** Where a debt on this row would be refused, mirroring the server's
 *  `carrierFor`: the row's own carrier when it has one, else the leg's. */
function debtCarrierProblem(
  owner: CarrierRef,
  leg: CarrierRef | undefined,
): PayRow['carrierNotInDirectory'] {
  if (leg === undefined) return typedOnly(owner) ? 'leg' : undefined
  if (hasCarrier(owner)) return typedOnly(owner) ? 'unit' : undefined
  return typedOnly(leg) ? 'leg' : undefined
}

/** One row from its lines, its own carrier (the unit's, or the leg's) and
 *  its rollup remainder. `leg` is the leg's carrier a unit falls back to;
 *  absent on «Плечо целиком», whose own carrier IS the leg's. */
function rowOf(
  base: Pick<PayRow, 'key' | 'unitId' | 'kind' | 'label'>,
  lines: readonly ExpenseLine[],
  owner: CarrierRef,
  rollup: Money | undefined,
  leg?: CarrierRef,
): PayRow {
  const target = targetOf(lines)
  const recognisesDebt = target?.kind === 'plan' || target?.kind === 'new'
  const carrierNotInDirectory = recognisesDebt ? debtCarrierProblem(owner, leg) : undefined
  const byVendor = target?.kind === 'actual'
  const carrierKey = byVendor ? carrierKeyOf(target.line.vendorId) : carrierKeyOf(owner.id)
  const carrierName = byVendor ? nameOf(target.line.counterpartyName) : nameOf(owner.name)
  const figure =
    target?.kind === 'plan'
      ? target.line.planned
      : target?.kind === 'actual'
        ? target.line.remaining
        : undefined
  return {
    ...base,
    target,
    carrierKey,
    carrierName,
    paid: target?.kind === 'actual' && target.line.settlement === 'paid',
    ...(carrierNotInDirectory ? { carrierNotInDirectory } : {}),
    remainder: target?.kind === 'plan' || target?.kind === 'actual' ? figure : rollup,
    seed: figure,
  }
}

/** Every row of the sheet: the units in their served order, then «Плечо
 *  целиком» — which is the leg's carrier's row (AC #12), so it is left out on
 *  a leg with no carrier at all (no directory id, no typed name) and no
 *  leg-level line: there is nobody it would be paid to, and listing it would
 *  conjure a «Перевозчик не из справочника» option no unit asked for. */
export function payRows(input: PayRowsInput): PayRow[] {
  const legCarrier = { id: input.legCarrierId, name: input.legCarrierName }
  const units = input.units.map((unit) =>
    rowOf(
      { key: unit.id, unitId: unit.id, kind: unit.kind, label: unit.number },
      input.expenses.filter((line) => line.unitId === unit.id),
      { id: unit.carrierId, name: unit.carrierName },
      input.perUnit.find((row) => row.unitId === unit.id)?.remaining,
      legCarrier,
    ),
  )
  const legLines = input.expenses.filter((line) => line.unitId === undefined)
  const legHasCarrier =
    carrierKeyOf(input.legCarrierId) !== OFF_DIRECTORY || nameOf(input.legCarrierName) !== ''
  if (!legHasCarrier && legLines.length === 0) return units
  const leg = rowOf({ key: WHOLE_LEG, label: '' }, legLines, legCarrier, input.legLevel?.remaining)
  return [...units, leg]
}

export interface CarrierOption {
  key: string
  name: string
}

/** The «Перевозчик» select: the leg's own carrier first (when it is a
 *  directory participant), then each other directory carrier the rows are
 *  listed under in row order, then «Перевозчик не из справочника» when any row
 *  is listed there. A name is the first non-empty one served for the key. */
export function carrierOptions(
  rows: readonly PayRow[],
  legCarrier: { id?: Maybe<string> | null; name?: Maybe<string> | null },
): CarrierOption[] {
  const options: CarrierOption[] = []
  const add = (key: string, name: string) => {
    const known = options.find((option) => option.key === key)
    if (!known) options.push({ key, name })
    else if (known.name === '' && name !== '') known.name = name
  }
  const legKey = carrierKeyOf(legCarrier.id)
  if (legKey !== OFF_DIRECTORY) add(legKey, nameOf(legCarrier.name))
  for (const row of rows) {
    if (row.carrierKey !== OFF_DIRECTORY) add(row.carrierKey, row.carrierName)
  }
  if (rows.some((row) => row.carrierKey === OFF_DIRECTORY)) {
    options.push({ key: OFF_DIRECTORY, name: '' })
  }
  return options
}
